import React, { useEffect, useState, useRef } from 'react';
import { Box, useToast } from '@chakra-ui/react';
import FullCalendar from '@fullcalendar/react';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { cqAPI } from '../../lib/Auth';

const AppointmentCalendar = () => {
  const toast = useToast();
  const calendarRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [resources, setResources] = useState([]);
  const [providers, setProviders] = useState([]);
  const [viewMode, setViewMode] = useState('provider');
  const colors = ['#e91e63', '#31b0d4', '#c18e00', '#0b6e3e', '#a50012', '#8bc34a'].sort((a, b) => (a < b ? -1 : 1));

  const fetchProviders = async () => {
    try {
      const response = await cqAPI.get('/providers');
      setProviders(response.data);
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  };

  const getEvents = async (date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    try {
      const data = await cqAPI.get(`/times/${month}/${day}/${year}`);
      if (data.type === 'error') {
        if (data.msg) {
          toast({
            title: "Error",
            description: data.msg,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
        return {};
      }

      const resourceMap = new Map();
      const resourcesList = [];
      const resourceEvents = [];

      data.data.forEach((appointment) => {
        let resourceId, resourceTitle;

        if (viewMode === 'provider') {
          resourceId = appointment.pid || appointment.provider_id;
          const provider = providers.find((p) => p.id === appointment.pid) || {};
          resourceTitle = appointment.pid
            ? provider.name
            : (appointment.provider_first_name
                ? `${appointment.provider_first_name} ${appointment.provider_last_name}`
                : 'Unknown');
          if (!resourceId) return; // ensure valid id in provider view too
        } else {
          resourceId = appointment.operatory_id;
          if (!resourceId) return; // <-- skip entries with no operatory_id
          resourceTitle = appointment.operatory_name || `Operatory ${appointment.operatory_id}` || 'Unknown';
        }

        if (!resourceMap.has(String(resourceId))) {
          resourceMap.set(String(resourceId), true);
          resourcesList.push({
            id: resourceId,
            title: resourceTitle,
          });
        }

        resourceEvents.push({
          resourceId: resourceId,
          title: appointment.patient_name,
          start: appointment.starttime,
          end: appointment.endtime,
          uid: appointment.patient_id,
          bid: appointment.bid,
          patient_guid: appointment.patient_guid,
          patient_id: appointment.patient_id
        });
      });

      resourcesList.sort((a, b) => (a.title < b.title ? -1 : 1));
      resourceEvents.forEach((e) => {
        e.backgroundColor = colors[resourcesList.findIndex(x => x.id === e.resourceId)];
      });

      return { resources: resourcesList, resourceEvents };
    } catch (error) {
      console.error(error);
    }
  };

  const handleDatesSet = async (info) => {
    const date = new Date(info.startStr);
    const results = await getEvents(date);
    setResources(results.resources);
    setEvents(results.resourceEvents);
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      handleDatesSet({ startStr: calendarApi.view.currentStart.toISOString() });
    }
  }, [viewMode]);

  const handleModeToggle = () => {
    setViewMode(prev => (prev === 'operatory' ? 'provider' : 'operatory'));
  };

  return (
    <Box p={4} w="100%" align="left">
      <FullCalendar
        ref={calendarRef}
        plugins={[resourceTimeGridPlugin, interactionPlugin]}
        schedulerLicenseKey="0147975619-fcs-1727372207"
        initialView="resourceTimeGridDay"
        headerToolbar={{
          right: 'modeToggle today prev,next',
          center: '',
          left: 'title'
        }}
        customButtons={{
          modeToggle: {
            text: viewMode === 'operatory' ? 'Provider View' : 'Operatory View',
            click: handleModeToggle
          }
        }}
        resources={resources}
        events={events}
        eventDidMount={(info) => { info.el.style.minHeight = '20px'; }}
        titleFormat={{ year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' }}
        slotMinTime="08:00:00"
        slotMaxTime="18:00:00"
        datesSet={handleDatesSet}
        eventClick={(info) => {
          const newTab = window.open('', '_blank');
          if (info.event.extendedProps.patient_id) {
            newTab.location.href = `/?bid=${info.event.extendedProps.bid}&_uid=${info.event.extendedProps.patient_id}`;
            return;
          }
          cqAPI.post('/pp/user', {
            to: `${info.event.extendedProps.bid}.${info.event.extendedProps.patient_guid}`
          }).then(result => {
            newTab.location.href = `/?bid=${info.event.extendedProps.bid}&_uid=${result.data}`;
          }).catch(error => {
            toast({
              title: "Error",
              description: error.message,
              status: "error",
              duration: 5000,
              isClosable: true,
            });
            newTab.close();
          });
        }}
      />
    </Box>
  );
};

export default AppointmentCalendar;
